import type { WebsiteDataFeedSelect } from 'types';
import type { FeedMatchMode, FeedPick, FeedRefreshSeconds } from 'definitions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';

export type FeedDraft = {
  name: string;
  url: string;
  refreshSeconds: FeedRefreshSeconds;
  pick: FeedPick;
  matchSource: string;
  matchField: string;
  matchMode: FeedMatchMode;
  startField: string;
  endField: string;
  /** The fields visitors' pages may read; the rest of each item stays on the server. */
  fields: string[];
};

export type FeedTestResult = { count: number; fields: string[]; sample: Record<string, string>[] };

/** The server answers a refused request with its reason as plain text (hono.ts onError). */
export const feedErrorMessage = (error: unknown) =>
  typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string'
    ? error.message
    : 'Something went wrong.';

/** A website's data feeds (P-PER-41): the list, and saving, deleting and testing one. */
export const useWebsiteFeeds = (websiteId: number) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const queryKey = ['WEBSITE_FEEDS', websiteId];

  const feedsQuery = useQuery<WebsiteDataFeedSelect[]>({
    queryKey,
    queryFn: () => apiClient.get(`/websites/${websiteId}/feeds`),
    enabled: !!websiteId,
  });

  const saveFeed = async (draft: FeedDraft, feedId?: number) => {
    if (feedId) await apiClient.put(`/websites/${websiteId}/feeds/${feedId}`, draft);
    else await apiClient.post(`/websites/${websiteId}/feeds`, draft);
    await queryClient.invalidateQueries({ queryKey });
  };

  const deleteFeed = async (feedId: number) => {
    await apiClient.delete(`/websites/${websiteId}/feeds/${feedId}`);
    await queryClient.invalidateQueries({ queryKey });
  };

  const testFeed = (url: string): Promise<FeedTestResult> =>
    apiClient.post(`/websites/${websiteId}/feeds/test`, { url });

  return {
    feeds: feedsQuery.data ?? [],
    isLoading: feedsQuery.isLoading,
    saveFeed,
    deleteFeed,
    testFeed,
  };
};
